import {
  PanelConfigInput,
  PanelFeatureEnum,
  PanelGroupInput,
  PanelItemDisplayTypeEnum,
  PanelItemFieldTypeEnum,
  PanelItemInput,
  PanelSectionInput,
  PanelTabInput,
} from '@/__generated__/graphql';
import { abyssColors } from '@/theme/colors';
import { DndContext, DragEndEvent, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import { SortableContext, useSortable, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Add, Delete, DragIndicator, Edit } from '@mui/icons-material';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Button,
  Checkbox,
  FormControl,
  FormControlLabel,
  Grid2 as Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { useAtom } from 'jotai';
import { useState } from 'react';
import { selectedPOIType } from '../../state';
import { REVIEW_PANEL_BLISTER_FIELDS, REVIEW_PANEL_POI_FIELDS } from './poi-field-definitions';
import { FullWidthPanel } from './styles';

const getFieldOptions = (
  fieldType: PanelItemFieldTypeEnum,
  templateFields: Array<{ key?: string | null; name: string }> | undefined
): { key: string; name: string }[] => {
  switch (fieldType) {
    case PanelItemFieldTypeEnum.PointOfInterest: {
      return REVIEW_PANEL_POI_FIELDS;
    }
    case PanelItemFieldTypeEnum.Blister: {
      return REVIEW_PANEL_BLISTER_FIELDS;
    }
    case PanelItemFieldTypeEnum.TemplateField: {
      return (templateFields ?? [])
        .filter((field): field is { key: string; name: string } => field.key !== null)
        .map((field) => ({ key: field.key, name: field.name }));
    }
  }
};

const labelToKey = (label: string) =>
  label
    .toLowerCase()
    .replace(/\s+/g, '_')
    .replace(/[^a-z0-9_]/g, '');

const FEATURE_OPTIONS = Object.values(PanelFeatureEnum);
const FIELD_TYPE_OPTIONS = Object.values(PanelItemFieldTypeEnum);
const DISPLAY_TYPE_OPTIONS = Object.values(PanelItemDisplayTypeEnum);

type DraftPanelConfig = PanelConfigInput;

const reorder = <T extends { order: number }>(items: T[], from: number, to: number): T[] => {
  const result = [...items];
  const [moved] = result.splice(from, 1);
  result.splice(to, 0, moved);
  return result.map((item, index) => ({ ...item, order: index }));
};

interface SortableDragHandleProps {
  listeners: Record<string, unknown> | undefined;
  attributes: Record<string, unknown>;
}

const SortableDragHandle = ({ listeners, attributes }: SortableDragHandleProps) => (
  <IconButton
    size="small"
    {...attributes}
    {...listeners}
    onClick={(event) => event.stopPropagation()}
    sx={{ cursor: 'grab', color: 'white', mr: 0.5 }}
  >
    <DragIndicator fontSize="small" />
  </IconButton>
);

export const PoiPanelConfigPanel = ({ handleSaveTemplate }: { handleSaveTemplate: () => void }) => {
  const [selectedType, setSelectedType] = useAtom(selectedPOIType);
  const [mode, setMode] = useState<'ReadOnly' | 'Edit'>('ReadOnly');
  const [draftConfig, setDraftConfig] = useState<DraftPanelConfig | null>(null);

  const isReadOnly = mode === 'ReadOnly';

  const sensors = useSensors(useSensor(PointerSensor));

  const handleEnterEdit = () => {
    const panelConfig = selectedType?.panelConfig;
    if (panelConfig) {
      const stripped: DraftPanelConfig = JSON.parse(JSON.stringify(panelConfig), (key, value) =>
        key === '__typename' || key === 'id' ? undefined : value
      );
      setDraftConfig(stripped);
    } else {
      setDraftConfig({ tabs: [] });
    }
    setMode('Edit');
  };

  const handleSave = () => {
    setSelectedType({ ...selectedType, panelConfig: draftConfig as any });
    setMode('ReadOnly');
    handleSaveTemplate();
  };

  const handleCancel = () => {
    setDraftConfig(null);
    setMode('ReadOnly');
  };

  const config = isReadOnly
    ? ((selectedType?.panelConfig as DraftPanelConfig | null | undefined) ?? { tabs: [] })
    : (draftConfig ?? { tabs: [] });

  // --- Tab mutations ---

  const addTab = () => {
    if (!draftConfig) return;
    const label = 'New Tab';
    const key = labelToKey(label) + '_' + draftConfig.tabs.length;
    setDraftConfig({
      ...draftConfig,
      tabs: [
        ...draftConfig.tabs,
        { key, label, order: draftConfig.tabs.length, feature: undefined, sections: [] },
      ],
    });
  };

  const deleteTab = (tabIndex: number) => {
    if (!draftConfig) return;
    setDraftConfig({
      ...draftConfig,
      tabs: draftConfig.tabs.filter((_, index) => index !== tabIndex),
    });
  };

  const updateTab = (tabIndex: number, updates: Partial<PanelTabInput>) => {
    if (!draftConfig) return;
    const tabs = draftConfig.tabs.map((tab, index) =>
      index === tabIndex ? { ...tab, ...updates } : tab
    );
    setDraftConfig({ ...draftConfig, tabs });
  };

  // --- Section mutations ---

  const addSection = (tabIndex: number) => {
    if (!draftConfig) return;
    const tab = draftConfig.tabs[tabIndex];
    const label = 'New Section';
    const key = labelToKey(label) + '_' + tab.sections.length;
    const newSection = {
      key,
      label,
      order: tab.sections.length,
      feature: undefined,
      openByDefault: false,
      isEditable: false,
      groups: [],
    };
    updateTab(tabIndex, { sections: [...tab.sections, newSection] });
  };

  const deleteSection = (tabIndex: number, sectionIndex: number) => {
    if (!draftConfig) return;
    const tab = draftConfig.tabs[tabIndex];
    updateTab(tabIndex, { sections: tab.sections.filter((_, i) => i !== sectionIndex) });
  };

  const updateSection = (
    tabIndex: number,
    sectionIndex: number,
    updates: Partial<PanelSectionInput>
  ) => {
    if (!draftConfig) return;
    const tab = draftConfig.tabs[tabIndex];
    const sections = tab.sections.map((section, i) =>
      i === sectionIndex ? { ...section, ...updates } : section
    );
    updateTab(tabIndex, { sections });
  };

  // --- Group mutations ---

  const addGroup = (tabIndex: number, sectionIndex: number) => {
    if (!draftConfig) return;
    const section = draftConfig.tabs[tabIndex].sections[sectionIndex];
    const groupNumber = section.groups.length + 1;
    const key = 'group_' + groupNumber;
    const newGroup = {
      key,
      label: undefined,
      order: section.groups.length,
      feature: undefined,
      items: [],
    };
    updateSection(tabIndex, sectionIndex, { groups: [...section.groups, newGroup] });
  };

  const deleteGroup = (tabIndex: number, sectionIndex: number, groupIndex: number) => {
    if (!draftConfig) return;
    const section = draftConfig.tabs[tabIndex].sections[sectionIndex];
    updateSection(tabIndex, sectionIndex, {
      groups: section.groups.filter((_, i) => i !== groupIndex),
    });
  };

  const updateGroup = (
    tabIndex: number,
    sectionIndex: number,
    groupIndex: number,
    updates: Partial<PanelGroupInput>
  ) => {
    if (!draftConfig) return;
    const section = draftConfig.tabs[tabIndex].sections[sectionIndex];
    const groups = section.groups.map((group, i) =>
      i === groupIndex ? { ...group, ...updates } : group
    );
    updateSection(tabIndex, sectionIndex, { groups });
  };

  // --- Item mutations ---

  const addItem = (tabIndex: number, sectionIndex: number, groupIndex: number) => {
    if (!draftConfig) return;
    const group = draftConfig.tabs[tabIndex].sections[sectionIndex].groups[groupIndex];
    const newItem = {
      key: REVIEW_PANEL_POI_FIELDS[0]?.key ?? '',
      fieldType: PanelItemFieldTypeEnum.PointOfInterest,
      displayType: PanelItemDisplayTypeEnum.Simple,
      label: undefined,
      feature: undefined,
      order: group.items.length,
    };
    updateGroup(tabIndex, sectionIndex, groupIndex, { items: [...group.items, newItem] });
  };

  const deleteItem = (
    tabIndex: number,
    sectionIndex: number,
    groupIndex: number,
    itemIndex: number
  ) => {
    if (!draftConfig) return;
    const group = draftConfig.tabs[tabIndex].sections[sectionIndex].groups[groupIndex];
    updateGroup(tabIndex, sectionIndex, groupIndex, {
      items: group.items.filter((_, i) => i !== itemIndex),
    });
  };

  const updateItem = (
    tabIndex: number,
    sectionIndex: number,
    groupIndex: number,
    itemIndex: number,
    updates: Partial<PanelItemInput>
  ) => {
    if (!draftConfig) return;
    const group = draftConfig.tabs[tabIndex].sections[sectionIndex].groups[groupIndex];
    const items = group.items.map((item, i) => (i === itemIndex ? { ...item, ...updates } : item));
    updateGroup(tabIndex, sectionIndex, groupIndex, { items });
  };

  // --- Reorder handlers ---

  const handleTabDragEnd = (event: DragEndEvent) => {
    if (!draftConfig) return;
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const from = draftConfig.tabs.findIndex((t) => t.key === active.id);
    const to = draftConfig.tabs.findIndex((t) => t.key === over.id);
    if (from === -1 || to === -1) return;
    setDraftConfig({ ...draftConfig, tabs: reorder(draftConfig.tabs, from, to) });
  };

  const handleSectionDragEnd = (tabIndex: number, event: DragEndEvent) => {
    if (!draftConfig) return;
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const tab = draftConfig.tabs[tabIndex];
    const from = tab.sections.findIndex((s) => s.key === active.id);
    const to = tab.sections.findIndex((s) => s.key === over.id);
    if (from === -1 || to === -1) return;
    updateTab(tabIndex, { sections: reorder(tab.sections, from, to) });
  };

  const handleGroupDragEnd = (tabIndex: number, sectionIndex: number, event: DragEndEvent) => {
    if (!draftConfig) return;
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const section = draftConfig.tabs[tabIndex].sections[sectionIndex];
    const from = section.groups.findIndex((g) => g.key === active.id);
    const to = section.groups.findIndex((g) => g.key === over.id);
    if (from === -1 || to === -1) return;
    updateSection(tabIndex, sectionIndex, { groups: reorder(section.groups, from, to) });
  };

  const handleItemDragEnd = (
    tabIndex: number,
    sectionIndex: number,
    groupIndex: number,
    event: DragEndEvent
  ) => {
    if (!draftConfig) return;
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const group = draftConfig.tabs[tabIndex].sections[sectionIndex].groups[groupIndex];
    const from = group.items.findIndex((it) => it.key === active.id);
    const to = group.items.findIndex((it) => it.key === over.id);
    if (from === -1 || to === -1) return;
    updateGroup(tabIndex, sectionIndex, groupIndex, { items: reorder(group.items, from, to) });
  };

  return (
    <>
      <Stack
        width="100%"
        marginTop="1.2rem"
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        height="4.8rem"
      >
        <Typography fontSize="2.2rem" fontWeight={500}>
          {`Panel Configuration for ${selectedType?.name}`}
        </Typography>
        {mode === 'Edit' ? (
          <Stack direction="row" spacing={1} sx={{ pb: 0.5 }}>
            <Button variant="contained" onClick={handleSave}>
              Save
            </Button>
            <Button variant="contained" onClick={handleCancel}>
              Cancel
            </Button>
          </Stack>
        ) : (
          <Stack direction="row" spacing={0} sx={{ pb: 0.5 }}>
            <Tooltip title="Edit Panel Configuration" placement="top" arrow>
              <IconButton size="medium" onClick={handleEnterEdit}>
                <Edit
                  fontSize="inherit"
                  sx={{ cursor: 'pointer', color: abyssColors.primary[500] }}
                />
              </IconButton>
            </Tooltip>
          </Stack>
        )}
      </Stack>

      <FullWidthPanel>
        <Stack sx={{ flex: 1, gap: '3.2rem', width: '100%' }}>
          <Stack gap={2}>
            <Typography variant="h4">Panel Tab Configuration</Typography>
            <Typography variant="body2" color="text.secondary">
              Configure the tabs, sections, groups, and items that appear in the POI detail panel
              for this template.
            </Typography>
          </Stack>

          <Stack gap={2}>
            <DndContext sensors={isReadOnly ? [] : sensors} onDragEnd={handleTabDragEnd}>
              <SortableContext
                items={config.tabs.map((t) => t.key)}
                strategy={verticalListSortingStrategy}
              >
                {config.tabs.map((tab, tabIndex) => (
                  <SortableTabAccordion
                    key={tab.key}
                    id={tab.key}
                    isReadOnly={isReadOnly}
                    tabSummary={
                      <Stack
                        direction="row"
                        alignItems="center"
                        justifyContent="space-between"
                        width="100%"
                        pr={1}
                      >
                        <Stack direction="row" alignItems="center" gap={1}>
                          <Typography color="white" fontWeight={500}>
                            {tab.label}
                          </Typography>
                          {isReadOnly ? (
                            <Typography variant="caption" color="white">
                              ({tab.key})
                            </Typography>
                          ) : (
                            <TextField
                              size="small"
                              value={tab.key}
                              onClick={(event) => event.stopPropagation()}
                              onChange={(event) => updateTab(tabIndex, { key: event.target.value })}
                              sx={{
                                '& .MuiInputBase-root': { color: 'white' },
                                '& .MuiOutlinedInput-notchedOutline': {
                                  borderColor: 'rgba(255,255,255,0.5)',
                                },
                                width: '14rem',
                              }}
                              inputProps={{
                                style: { fontSize: '1.2rem', padding: '0.2rem 0.6rem' },
                              }}
                            />
                          )}
                        </Stack>
                        {!isReadOnly && (
                          <IconButton
                            size="small"
                            onClick={(event) => {
                              event.stopPropagation();
                              deleteTab(tabIndex);
                            }}
                          >
                            <Delete fontSize="small" sx={{ color: 'white' }} />
                          </IconButton>
                        )}
                      </Stack>
                    }
                    summaryBg={abyssColors.primary[500]}
                  >
                    <AccordionDetails sx={{ padding: '2.4rem' }}>
                      <Stack gap={2} mb={2}>
                        <TextField
                          label="Label"
                          size="small"
                          disabled={isReadOnly}
                          value={tab.label}
                          onChange={(event) => {
                            updateTab(tabIndex, { label: event.target.value });
                          }}
                        />
                        <FormControl size="small" disabled={isReadOnly}>
                          <InputLabel>Feature</InputLabel>
                          <Select
                            label="Feature"
                            value={tab.feature ?? ''}
                            onChange={(event) => {
                              const value = event.target.value;
                              updateTab(tabIndex, {
                                feature: value === '' ? undefined : (value as PanelFeatureEnum),
                              });
                            }}
                          >
                            <MenuItem value="">None</MenuItem>
                            {FEATURE_OPTIONS.map((option) => (
                              <MenuItem key={option} value={option}>
                                {option}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      </Stack>

                      <Stack gap={1}>
                        <Typography variant="h5" color="primary.500">
                          Sections
                        </Typography>
                        <DndContext
                          sensors={isReadOnly ? [] : sensors}
                          onDragEnd={(event) => handleSectionDragEnd(tabIndex, event)}
                        >
                          <SortableContext
                            items={tab.sections.map((s) => s.key)}
                            strategy={verticalListSortingStrategy}
                          >
                            {tab.sections.map((section, sectionIndex) => (
                              <SortableTabAccordion
                                key={section.key}
                                id={section.key}
                                isReadOnly={isReadOnly}
                                tabSummary={
                                  <Stack
                                    direction="row"
                                    alignItems="center"
                                    justifyContent="space-between"
                                    width="100%"
                                    pr={1}
                                  >
                                    <Stack direction="row" alignItems="center" gap={1}>
                                      <Typography color="white" fontWeight={500}>
                                        {section.label}
                                      </Typography>
                                      {isReadOnly ? (
                                        <Typography variant="caption" color="white">
                                          ({section.key})
                                        </Typography>
                                      ) : (
                                        <TextField
                                          size="small"
                                          value={section.key}
                                          onClick={(event) => event.stopPropagation()}
                                          onChange={(event) =>
                                            updateSection(tabIndex, sectionIndex, {
                                              key: event.target.value,
                                            })
                                          }
                                          sx={{
                                            '& .MuiInputBase-root': { color: 'white' },
                                            '& .MuiOutlinedInput-notchedOutline': {
                                              borderColor: 'rgba(255,255,255,0.5)',
                                            },
                                            width: '14rem',
                                          }}
                                          inputProps={{
                                            style: { fontSize: '1.2rem', padding: '0.2rem 0.6rem' },
                                          }}
                                        />
                                      )}
                                    </Stack>
                                    {!isReadOnly && (
                                      <IconButton
                                        size="small"
                                        onClick={(event) => {
                                          event.stopPropagation();
                                          deleteSection(tabIndex, sectionIndex);
                                        }}
                                      >
                                        <Delete fontSize="small" sx={{ color: 'white' }} />
                                      </IconButton>
                                    )}
                                  </Stack>
                                }
                                summaryBg={abyssColors.primary[400]}
                                sx={{ marginLeft: '2.4rem' }}
                              >
                                <AccordionDetails sx={{ padding: '2.4rem' }}>
                                  <Stack gap={2} mb={2}>
                                    <TextField
                                      label="Label"
                                      size="small"
                                      disabled={isReadOnly}
                                      value={section.label}
                                      onChange={(event) => {
                                        updateSection(tabIndex, sectionIndex, {
                                          label: event.target.value,
                                        });
                                      }}
                                    />
                                    <FormControl size="small" disabled={isReadOnly}>
                                      <InputLabel>Feature</InputLabel>
                                      <Select
                                        label="Feature"
                                        value={section.feature ?? ''}
                                        onChange={(event) => {
                                          const value = event.target.value;
                                          updateSection(tabIndex, sectionIndex, {
                                            feature:
                                              value === ''
                                                ? undefined
                                                : (value as PanelFeatureEnum),
                                          });
                                        }}
                                      >
                                        <MenuItem value="">None</MenuItem>
                                        {FEATURE_OPTIONS.map((option) => (
                                          <MenuItem key={option} value={option}>
                                            {option}
                                          </MenuItem>
                                        ))}
                                      </Select>
                                    </FormControl>
                                    <Stack direction="row" gap={2}>
                                      <FormControlLabel
                                        control={
                                          <Checkbox
                                            size="small"
                                            disabled={isReadOnly}
                                            checked={section.openByDefault ?? false}
                                            onChange={(event) =>
                                              updateSection(tabIndex, sectionIndex, {
                                                openByDefault: event.target.checked,
                                              })
                                            }
                                          />
                                        }
                                        label={
                                          <Typography variant="body2">Open by Default</Typography>
                                        }
                                      />
                                      <FormControlLabel
                                        control={
                                          <Checkbox
                                            size="small"
                                            disabled={isReadOnly}
                                            checked={section.isEditable ?? false}
                                            onChange={(event) =>
                                              updateSection(tabIndex, sectionIndex, {
                                                isEditable: event.target.checked,
                                              })
                                            }
                                          />
                                        }
                                        label={<Typography variant="body2">Is Editable</Typography>}
                                      />
                                    </Stack>
                                  </Stack>

                                  <Stack gap={1}>
                                    <Typography variant="h5" color="primary.500">
                                      Groups
                                    </Typography>
                                    <DndContext
                                      sensors={isReadOnly ? [] : sensors}
                                      onDragEnd={(event) =>
                                        handleGroupDragEnd(tabIndex, sectionIndex, event)
                                      }
                                    >
                                      <SortableContext
                                        items={section.groups.map((g) => g.key)}
                                        strategy={verticalListSortingStrategy}
                                      >
                                        {section.groups.map((group, groupIndex) => (
                                          <SortableTabAccordion
                                            key={group.key}
                                            id={group.key}
                                            isReadOnly={isReadOnly}
                                            tabSummary={
                                              <Stack
                                                direction="row"
                                                alignItems="center"
                                                justifyContent="space-between"
                                                width="100%"
                                                pr={1}
                                              >
                                                <Stack direction="row" alignItems="center" gap={1}>
                                                  <Typography color="white" fontWeight={500}>
                                                    {group.label ?? `Group ${groupIndex + 1}`}
                                                  </Typography>
                                                  {isReadOnly ? (
                                                    <Typography variant="caption" color="white">
                                                      ({group.key})
                                                    </Typography>
                                                  ) : (
                                                    <TextField
                                                      size="small"
                                                      value={group.key}
                                                      onClick={(event) => event.stopPropagation()}
                                                      onChange={(event) =>
                                                        updateGroup(
                                                          tabIndex,
                                                          sectionIndex,
                                                          groupIndex,
                                                          { key: event.target.value }
                                                        )
                                                      }
                                                      sx={{
                                                        '& .MuiInputBase-root': { color: 'white' },
                                                        '& .MuiOutlinedInput-notchedOutline': {
                                                          borderColor: 'rgba(255,255,255,0.5)',
                                                        },
                                                        width: '14rem',
                                                      }}
                                                      inputProps={{
                                                        style: {
                                                          fontSize: '1.2rem',
                                                          padding: '0.2rem 0.6rem',
                                                        },
                                                      }}
                                                    />
                                                  )}
                                                </Stack>
                                                {!isReadOnly && (
                                                  <IconButton
                                                    size="small"
                                                    onClick={(event) => {
                                                      event.stopPropagation();
                                                      deleteGroup(
                                                        tabIndex,
                                                        sectionIndex,
                                                        groupIndex
                                                      );
                                                    }}
                                                  >
                                                    <Delete fontSize="small" />
                                                  </IconButton>
                                                )}
                                              </Stack>
                                            }
                                            summaryBg={abyssColors.primary[400]}
                                            sx={{ marginLeft: '2.4rem' }}
                                          >
                                            <AccordionDetails sx={{ padding: '2.4rem' }}>
                                              <Stack gap={2} mb={2}>
                                                <TextField
                                                  label="Label (optional)"
                                                  size="small"
                                                  disabled={isReadOnly}
                                                  value={group.label ?? ''}
                                                  onChange={(event) =>
                                                    updateGroup(
                                                      tabIndex,
                                                      sectionIndex,
                                                      groupIndex,
                                                      {
                                                        label: event.target.value || undefined,
                                                      }
                                                    )
                                                  }
                                                />
                                                <FormControl size="small" disabled={isReadOnly}>
                                                  <InputLabel>Feature</InputLabel>
                                                  <Select
                                                    label="Feature"
                                                    value={group.feature ?? ''}
                                                    onChange={(event) => {
                                                      const value = event.target.value;
                                                      updateGroup(
                                                        tabIndex,
                                                        sectionIndex,
                                                        groupIndex,
                                                        {
                                                          feature:
                                                            value === ''
                                                              ? undefined
                                                              : (value as PanelFeatureEnum),
                                                        }
                                                      );
                                                    }}
                                                  >
                                                    <MenuItem value="">None</MenuItem>
                                                    {FEATURE_OPTIONS.map((option) => (
                                                      <MenuItem key={option} value={option}>
                                                        {option}
                                                      </MenuItem>
                                                    ))}
                                                  </Select>
                                                </FormControl>
                                              </Stack>

                                              {/* Items table */}
                                              <Stack gap={1}>
                                                <Typography
                                                  variant="subtitle2"
                                                  color="text.secondary"
                                                >
                                                  Items
                                                </Typography>
                                                {group.items.length > 0 && (
                                                  <Grid container spacing={1} alignItems="center">
                                                    {!isReadOnly && <Grid size={0.5} />}
                                                    <Grid size={isReadOnly ? 2 : 1.5}>
                                                      <Typography
                                                        variant="caption"
                                                        fontWeight={600}
                                                      >
                                                        Key
                                                      </Typography>
                                                    </Grid>
                                                    <Grid size={2}>
                                                      <Typography
                                                        variant="caption"
                                                        fontWeight={600}
                                                      >
                                                        Field Type
                                                      </Typography>
                                                    </Grid>
                                                    <Grid size={2}>
                                                      <Typography
                                                        variant="caption"
                                                        fontWeight={600}
                                                      >
                                                        Display Type
                                                      </Typography>
                                                    </Grid>
                                                    <Grid size={3}>
                                                      <Typography
                                                        variant="caption"
                                                        fontWeight={600}
                                                      >
                                                        Label
                                                      </Typography>
                                                    </Grid>
                                                    <Grid size={2}>
                                                      <Typography
                                                        variant="caption"
                                                        fontWeight={600}
                                                      >
                                                        Feature
                                                      </Typography>
                                                    </Grid>
                                                    <Grid size={1} />
                                                  </Grid>
                                                )}
                                                <DndContext
                                                  sensors={isReadOnly ? [] : sensors}
                                                  onDragEnd={(event) =>
                                                    handleItemDragEnd(
                                                      tabIndex,
                                                      sectionIndex,
                                                      groupIndex,
                                                      event
                                                    )
                                                  }
                                                >
                                                  <SortableContext
                                                    items={group.items.map((it) => it.key)}
                                                    strategy={verticalListSortingStrategy}
                                                  >
                                                    {group.items.map((item, itemIndex) => (
                                                      <SortableItemRow
                                                        key={item.key}
                                                        id={item.key}
                                                        isReadOnly={isReadOnly}
                                                      >
                                                        <Grid size={isReadOnly ? 2 : 1.5}>
                                                          {isReadOnly ? (
                                                            <Typography
                                                              variant="caption"
                                                              color="text.secondary"
                                                            >
                                                              {item.key}
                                                            </Typography>
                                                          ) : (
                                                            <FormControl size="small" fullWidth>
                                                              <Select
                                                                value={item.key}
                                                                onChange={(event) =>
                                                                  updateItem(
                                                                    tabIndex,
                                                                    sectionIndex,
                                                                    groupIndex,
                                                                    itemIndex,
                                                                    { key: event.target.value }
                                                                  )
                                                                }
                                                              >
                                                                {getFieldOptions(
                                                                  item.fieldType,
                                                                  selectedType?.fields
                                                                ).map((option) => (
                                                                  <MenuItem
                                                                    key={option.key}
                                                                    value={option.key}
                                                                  >
                                                                    {option.name}
                                                                  </MenuItem>
                                                                ))}
                                                              </Select>
                                                            </FormControl>
                                                          )}
                                                        </Grid>
                                                        <Grid size={2}>
                                                          <FormControl
                                                            size="small"
                                                            fullWidth
                                                            disabled={isReadOnly}
                                                          >
                                                            <Select
                                                              value={item.fieldType}
                                                              onChange={(event) => {
                                                                const newFieldType = event.target
                                                                  .value as PanelItemFieldTypeEnum;
                                                                const newOptions = getFieldOptions(
                                                                  newFieldType,
                                                                  selectedType?.fields
                                                                );
                                                                updateItem(
                                                                  tabIndex,
                                                                  sectionIndex,
                                                                  groupIndex,
                                                                  itemIndex,
                                                                  {
                                                                    fieldType: newFieldType,
                                                                    key: newOptions[0]?.key ?? '',
                                                                  }
                                                                );
                                                              }}
                                                            >
                                                              {FIELD_TYPE_OPTIONS.map((option) => (
                                                                <MenuItem
                                                                  key={option}
                                                                  value={option}
                                                                >
                                                                  {option}
                                                                </MenuItem>
                                                              ))}
                                                            </Select>
                                                          </FormControl>
                                                        </Grid>
                                                        <Grid size={2}>
                                                          <FormControl
                                                            size="small"
                                                            fullWidth
                                                            disabled={isReadOnly}
                                                          >
                                                            <Select
                                                              value={item.displayType}
                                                              onChange={(event) =>
                                                                updateItem(
                                                                  tabIndex,
                                                                  sectionIndex,
                                                                  groupIndex,
                                                                  itemIndex,
                                                                  {
                                                                    displayType: event.target
                                                                      .value as PanelItemDisplayTypeEnum,
                                                                  }
                                                                )
                                                              }
                                                            >
                                                              {DISPLAY_TYPE_OPTIONS.map(
                                                                (option) => (
                                                                  <MenuItem
                                                                    key={option}
                                                                    value={option}
                                                                  >
                                                                    {option}
                                                                  </MenuItem>
                                                                )
                                                              )}
                                                            </Select>
                                                          </FormControl>
                                                        </Grid>
                                                        <Grid size={3}>
                                                          <TextField
                                                            size="small"
                                                            fullWidth
                                                            disabled={isReadOnly}
                                                            value={item.label ?? ''}
                                                            placeholder="Label"
                                                            onChange={(event) =>
                                                              updateItem(
                                                                tabIndex,
                                                                sectionIndex,
                                                                groupIndex,
                                                                itemIndex,
                                                                {
                                                                  label:
                                                                    event.target.value || undefined,
                                                                }
                                                              )
                                                            }
                                                          />
                                                        </Grid>
                                                        <Grid size={2}>
                                                          <FormControl
                                                            size="small"
                                                            fullWidth
                                                            disabled={isReadOnly}
                                                          >
                                                            <Select
                                                              value={item.feature ?? ''}
                                                              onChange={(event) => {
                                                                const value = event.target.value;
                                                                updateItem(
                                                                  tabIndex,
                                                                  sectionIndex,
                                                                  groupIndex,
                                                                  itemIndex,
                                                                  {
                                                                    feature:
                                                                      value === ''
                                                                        ? undefined
                                                                        : (value as PanelFeatureEnum),
                                                                  }
                                                                );
                                                              }}
                                                            >
                                                              <MenuItem value="">None</MenuItem>
                                                              {FEATURE_OPTIONS.map((option) => (
                                                                <MenuItem
                                                                  key={option}
                                                                  value={option}
                                                                >
                                                                  {option}
                                                                </MenuItem>
                                                              ))}
                                                            </Select>
                                                          </FormControl>
                                                        </Grid>
                                                        <Grid size={1}>
                                                          <IconButton
                                                            size="small"
                                                            disabled={isReadOnly}
                                                            onClick={() =>
                                                              deleteItem(
                                                                tabIndex,
                                                                sectionIndex,
                                                                groupIndex,
                                                                itemIndex
                                                              )
                                                            }
                                                          >
                                                            <Delete fontSize="small" />
                                                          </IconButton>
                                                        </Grid>
                                                      </SortableItemRow>
                                                    ))}
                                                  </SortableContext>
                                                </DndContext>
                                                {!isReadOnly && (
                                                  <Button
                                                    size="small"
                                                    startIcon={<Add />}
                                                    onClick={() =>
                                                      addItem(tabIndex, sectionIndex, groupIndex)
                                                    }
                                                    sx={{ alignSelf: 'flex-start', mt: 1 }}
                                                  >
                                                    Add Item
                                                  </Button>
                                                )}
                                              </Stack>
                                            </AccordionDetails>
                                          </SortableTabAccordion>
                                        ))}
                                      </SortableContext>
                                    </DndContext>
                                    {!isReadOnly && (
                                      <Button
                                        size="small"
                                        startIcon={<Add />}
                                        onClick={() => addGroup(tabIndex, sectionIndex)}
                                        sx={{ alignSelf: 'flex-start' }}
                                      >
                                        Add Group
                                      </Button>
                                    )}
                                  </Stack>
                                </AccordionDetails>
                              </SortableTabAccordion>
                            ))}
                          </SortableContext>
                        </DndContext>
                        {!isReadOnly && (
                          <Button
                            size="small"
                            startIcon={<Add />}
                            onClick={() => addSection(tabIndex)}
                            sx={{ alignSelf: 'flex-start' }}
                          >
                            Add Section
                          </Button>
                        )}
                      </Stack>
                    </AccordionDetails>
                  </SortableTabAccordion>
                ))}
              </SortableContext>
            </DndContext>
          </Stack>

          {!isReadOnly && (
            <Button
              variant="outlined"
              startIcon={<Add />}
              onClick={addTab}
              sx={{ alignSelf: 'flex-start' }}
            >
              Add Tab
            </Button>
          )}
        </Stack>
      </FullWidthPanel>
    </>
  );
};

interface SortableTabAccordionProps {
  id: string;
  isReadOnly: boolean;
  tabSummary: React.ReactNode;
  summaryBg: string;
  sx?: Record<string, unknown>;
  children: React.ReactNode;
}

const SortableTabAccordion = ({
  id,
  isReadOnly,
  tabSummary,
  summaryBg,
  sx,
  children,
}: SortableTabAccordionProps) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id,
    disabled: isReadOnly,
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <Accordion
      ref={setNodeRef}
      style={style}
      elevation={0}
      disableGutters
      sx={{ border: `0.1rem solid ${abyssColors.primary[300]}`, ...sx }}
    >
      <AccordionSummary
        expandIcon={<ExpandMoreIcon />}
        sx={{
          background: summaryBg,
          '& .MuiSvgIcon-root': { color: 'white' },
        }}
      >
        <Stack direction="row" alignItems="center" width="100%">
          {!isReadOnly && (
            <SortableDragHandle listeners={listeners as any} attributes={attributes as any} />
          )}
          {tabSummary}
        </Stack>
      </AccordionSummary>
      {children}
    </Accordion>
  );
};

interface SortableItemRowProps {
  id: string;
  isReadOnly: boolean;
  children: React.ReactNode;
}

const SortableItemRow = ({ id, isReadOnly, children }: SortableItemRowProps) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id,
    disabled: isReadOnly,
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <Grid ref={setNodeRef} style={style} container spacing={1} alignItems="center">
      {!isReadOnly && (
        <Grid size={0.5}>
          <SortableDragHandle listeners={listeners as any} attributes={attributes as any} />
        </Grid>
      )}
      {children}
    </Grid>
  );
};
